import type { SwitchboardPathFlow, SwitchboardResponse } from "@t3tools/contracts";
import { ChartPanel, DataTable } from "./dashboardKit";
import { switchboardCallPathLabel } from "./switchboardOutcomes";
import { SwitchboardPathTree } from "./SwitchboardPathTree";

function formatReviewedAt(value: string | null, timezone: string) {
  return value
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}

export function SwitchboardPathReview({
  data,
}: {
  data: Pick<
    SwitchboardResponse,
    "pathReviewSummary" | "pathFlows" | "pathTreeCounts" | "summary" | "timezone"
  >;
}) {
  const summary = data.pathReviewSummary;
  const flows = data.pathFlows;
  const treeCounts = data.pathTreeCounts;

  if (!summary || !flows || !treeCounts) {
    return (
      <ChartPanel title="Switchboard call path tree">
        <p role="status" className="text-sm text-muted-foreground">
          Path review data is unavailable in this API response.
        </p>
      </ChartPanel>
    );
  }

  const coverage = data.summary.totalCalls
    ? Math.round((summary.reviewedCalls / data.summary.totalCalls) * 100)
    : 0;
  const promptVersions = summary.promptVersions.map((version) => `v${version}`).join(", ") || "—";

  return (
    <ChartPanel title="Switchboard call path tree">
      <p className="mb-4 max-w-4xl text-xs leading-relaxed text-muted-foreground">
        The tree follows the shared path model across filtered calls. Counts combine recorded call
        outcomes with reviewed path fields; unknown branches stay distinct from zero-count nodes.
      </p>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
          <p className="text-xs text-muted-foreground">Reviewed</p>
          <p className="mt-1 text-lg font-semibold text-foreground">
            {summary.reviewedCalls.toLocaleString()} / {data.summary.totalCalls.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">{coverage}% of filtered attempts</p>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
          <p className="text-xs text-muted-foreground">Path disagreements</p>
          <p className="mt-1 text-lg font-semibold text-foreground">
            {summary.callsWithDisagreement.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Flagged against recorded call facts</p>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
          <p className="text-xs text-muted-foreground">Review prompt</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{promptVersions}</p>
          <p className="text-xs text-muted-foreground">Versions in the filtered results</p>
        </div>
        <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
          <p className="text-xs text-muted-foreground">Latest review</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {formatReviewedAt(summary.latestReviewedAt, data.timezone)}
          </p>
          <p className="text-xs text-muted-foreground">{data.timezone}</p>
        </div>
      </div>
      <SwitchboardPathTree counts={treeCounts} totalCalls={data.summary.totalCalls} />
      {flows.length ? (
        <div className="mt-5 border-t border-border/60 pt-4">
          <h4 className="mb-3 text-xs font-semibold text-foreground">Path combination details</h4>
          <DataTable<SwitchboardPathFlow>
            rows={[...flows]}
            rowKey={(row) =>
              JSON.stringify([row.firstAnswerer, row.screenerDecision, row.handoffFailureReason])
            }
            maxHeightClass="max-h-[30rem]"
            columns={[
              {
                key: "firstAnswerer",
                label: "First answerer",
                render: (row) =>
                  switchboardCallPathLabel(row.firstAnswerer, "firstAnswerer", "Unknown"),
              },
              {
                key: "screenerDecision",
                label: "Screener decision",
                render: (row) => switchboardCallPathLabel(row.screenerDecision, "screenerDecision"),
              },
              {
                key: "handoffFailureReason",
                label: "Handoff failure reason",
                render: (row) =>
                  switchboardCallPathLabel(row.handoffFailureReason, "handoffFailureReason"),
              },
              {
                key: "count",
                label: "Calls",
                align: "right",
                render: (row) => {
                  const share = summary.reviewedCalls
                    ? Math.min(100, (row.count / summary.reviewedCalls) * 100)
                    : 0;
                  return (
                    <div className="min-w-24">
                      <p className="text-right font-medium">{row.count.toLocaleString()}</p>
                      <div
                        className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
                        aria-label={`${share.toFixed(1)}% of reviewed calls`}
                      >
                        <div
                          className="h-full rounded-full bg-sky-600"
                          style={{ width: `${share}%` }}
                        />
                      </div>
                    </div>
                  );
                },
              },
              {
                key: "disagreementCount",
                label: "Disagreement flags",
                align: "right",
                render: (row) =>
                  row.disagreementCount ? (
                    <span className="font-medium text-amber-700 dark:text-amber-400">
                      {row.disagreementCount.toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  ),
              },
            ]}
          />
        </div>
      ) : null}
    </ChartPanel>
  );
}
